import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Button, Select } from '@tadagram/ui';
import { User, MessageCircle, Heart } from 'lucide-react';
import { TagSelector } from './TagSelector';
import type { SeedingProfile, RoleDefinition, ConversationStyle, SeedingRole } from '../types';
import { apiClient } from '../api/client';
import { getDemographicOptions, type DemographicOptions } from '../services/library';

export interface DemographicsData {
  seeding_profile?: SeedingProfile;
}

interface DemographicsFormProps {
  initialData?: DemographicsData;
  onSave: (data: DemographicsData) => void;
  onCancel: () => void;
}

export const DemographicsForm: React.FC<DemographicsFormProps> = ({
  initialData,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation('account');
  const [roles, setRoles] = useState<SeedingRole[]>([]);
  const [demoOptions, setDemoOptions] = useState<DemographicOptions | null>(null);
  const [formState, setFormState] = useState({
    // Role
    role_name: initialData?.seeding_profile?.role?.role_name || '',
    
    // Basic identity
    age: initialData?.seeding_profile?.age || 28,
    gender: initialData?.seeding_profile?.gender || 'female',
    location: initialData?.seeding_profile?.location || 'Ho Chi Minh, Vietnam',
    
    // Conversation style
    tone: initialData?.seeding_profile?.conversation_style?.tone || 'casual',
    emoji_usage: initialData?.seeding_profile?.conversation_style?.emoji_usage || 'moderate',
    response_length: initialData?.seeding_profile?.conversation_style?.response_length || 'medium',
    vocabulary: initialData?.seeding_profile?.conversation_style?.vocabulary || 'casual',
    forms_of_address: initialData?.seeding_profile?.conversation_style?.forms_of_address || 'friendly',
    
    // Interests as array (no longer comma-separated string)
    interests: initialData?.seeding_profile?.interests || [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load roles from API
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const rolesData = await apiClient.getRoles();
        setRoles(rolesData);
        // Set default role if not set
        if (!formState.role_name && rolesData.length > 0) {
          setFormState(prev => ({ ...prev, role_name: rolesData[0].role_name }));
        }
      } catch (error) {
        console.error('Failed to load roles:', error);
      }
    };
    loadRoles();
  }, []);

  // Load conversation style options from API (with cache + fallback)
  useEffect(() => {
    getDemographicOptions().then(setDemoOptions);
  }, []);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formState.role_name) {
      newErrors.role_name = 'Please select a role';
    }

    if (formState.age < 13 || formState.age > 100) {
      newErrors.age = 'Age must be between 13-100';
    }

    if (!formState.location.trim()) {
      newErrors.location = 'Location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    // 🔧 CRITICAL FIX: Include role_id from selected role
    const selectedRole = roles.find(r => r.role_name === formState.role_name);
    
    const role: RoleDefinition = {
      role_id: selectedRole?.role_id || '', // Include role_id for backend
      role_name: formState.role_name,
      command: selectedRole?.command || '',
      needs_brand_info: selectedRole?.needs_brand_info || false,
    };

    const conversationStyle: ConversationStyle = {
      tone: formState.tone,
      emoji_usage: formState.emoji_usage,
      response_length: formState.response_length,
      vocabulary: formState.vocabulary,
      forms_of_address: formState.forms_of_address,
    };

    const seedingProfile: SeedingProfile = {
      role,
      age: formState.age,
      gender: formState.gender,
      location: formState.location,
      conversation_style: conversationStyle,
      interests: formState.interests, // Already an array
    };

    onSave({ seeding_profile: seedingProfile });
  };

  return (
    <form id="demographics-form" onSubmit={handleSubmit} className="space-y-6">
      {/* Role & Identity */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-tg-text flex items-center gap-2">
          <User size={20} className="text-blue-500" />
          Role & Identity
        </h3>
        
        <div className="grid grid-cols-1 gap-4">
          <Select
            label={`${t('role')} *`}
            value={formState.role_name}
            onChange={(e) => setFormState(prev => ({ 
              ...prev, 
              role_name: e.target.value
            }))}
            options={roles.map(role => ({
              value: role.role_name,
              label: role.role_name
            }))}
            error={errors.role_name}
            required
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-tg-text mb-2">{t('age')} *</label>
            <Input
              type="number"
              value={formState.age}
              onChange={(e) => setFormState(prev => ({ ...prev, age: parseInt(e.target.value) || 28 }))}
              min={13}
              max={100}
              error={errors.age}
              required
            />
          </div>

          <Select
            label={`${t('gender')} *`}
            value={formState.gender}
            onChange={(e) => setFormState(prev => ({ ...prev, gender: e.target.value }))}
            options={[
              { value: 'male', label: t('male') },
              { value: 'female', label: t('female') },
              { value: 'other', label: t('other') },
            ]}
            required
          />

          <div>
            <label className="block text-sm font-medium text-tg-text mb-2">{t('location')} *</label>
            <Input
              value={formState.location}
              onChange={(e) => setFormState(prev => ({ ...prev, location: e.target.value }))}
              placeholder="Ho Chi Minh, Vietnam"
              error={errors.location}
              required
            />
          </div>
        </div>
      </div>

      {/* Conversation Style */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-tg-text flex items-center gap-2">
          <MessageCircle size={20} className="text-purple-500" />
          Conversation Style
        </h3>

        <div className="grid grid-cols-2 gap-4">
          <Select
            label={t('tone')}
            value={formState.tone}
            onChange={(e) => setFormState(prev => ({ ...prev, tone: e.target.value }))}
            options={(demoOptions?.tone ?? []).map(o => ({ value: o.value, label: o.label }))}
          />

          <Select
            label={t('emojiUsage')}
            value={formState.emoji_usage}
            onChange={(e) => setFormState(prev => ({ ...prev, emoji_usage: e.target.value }))}
            options={(demoOptions?.emoji_usage ?? []).map(o => ({ value: o.value, label: o.label }))}
          />

          <Select
            label={t('responseLength')}
            value={formState.response_length}
            onChange={(e) => setFormState(prev => ({ ...prev, response_length: e.target.value }))}
            options={(demoOptions?.response_length ?? []).map(o => ({ value: o.value, label: o.label }))}
          />

          <Select
            label={t('vocabulary')}
            value={formState.vocabulary}
            onChange={(e) => setFormState(prev => ({ ...prev, vocabulary: e.target.value }))}
            options={(demoOptions?.vocabulary ?? []).map(o => ({ value: o.value, label: o.label }))}
          />

          <div className="col-span-2">
            <Select
              label={t('formsOfAddress')}
              value={formState.forms_of_address}
              onChange={(e) => setFormState(prev => ({ ...prev, forms_of_address: e.target.value }))}
              options={(demoOptions?.forms_of_address ?? []).map(o => ({ value: o.value, label: o.label }))}
            />
            <p className="text-xs text-tg-hint mt-1">
              {t('formsOfAddressHelper')}
            </p>
          </div>
        </div>
      </div>

      {/* Interests */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-tg-text flex items-center gap-2">
          <Heart size={20} className="text-pink-500" />
          Interests
        </h3>

        <div>
          <label className="block text-sm font-medium text-tg-text mb-2">
            {t('selectYourInterests')}
          </label>
          <TagSelector
            value={formState.interests}
            onChange={(tags) => setFormState(prev => ({ ...prev, interests: tags }))}
            placeholder={t('clickTagsToAdd')}
          />
          <p className="text-xs text-tg-hint mt-2">
            {t('interestsHelper')}
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary">
          {t('saveDemographics')}
        </Button>
      </div>
    </form>
  );
};
